/**
 * MastraAuthWorkos - WorkOS authentication provider for Mastra.
 *
 * Uses @workos/authkit-session for session management with encrypted
 * cookie-based sessions that persist across server restarts.
 */

import type {
  IUserProvider,
  ISSOProvider,
  ISessionProvider,
  Session,
  SSOCallbackResult,
  SSOLoginConfig,
} from '@internal/auth';
import type { EEUser } from '@internal/auth/ee';
import type { MastraAuthProviderOptions } from '@internal/auth/provider';
import { MastraAuthProvider } from '@internal/auth/provider';
import { verifyJwks } from '@mastra/auth';
import type { JwtPayload } from '@mastra/auth';
import { AuthService, sessionEncryption } from '@workos/authkit-session';
import type { AuthKitConfig } from '@workos/authkit-session';
import { WorkOS } from '@workos-inc/node';
import type { OrganizationMembership } from '@workos-inc/node';
import { LRUCache } from 'lru-cache';

type HonoRequestLike = {
  raw?: Request;
  headers?: Headers;
  header(name: string): string | undefined;
};

type MastraAuthRequest = Request | HonoRequestLike;

function getWebRequest(request: MastraAuthRequest): Request | undefined {
  if (request instanceof Request) {
    return request;
  }

  return request.raw instanceof Request ? request.raw : undefined;
}

import { WebSessionStorage } from './session-storage.js';
import type { WorkOSUser, MastraAuthWorkosOptions } from './types.js';
import { mapWorkOSUserToEEUser } from './types.js';

/**
 * Default cookie password for development (MUST be overridden in production).
 * Generated once per process to ensure consistency during dev.
 */
const DEV_COOKIE_PASSWORD = crypto.randomUUID() + crypto.randomUUID(); // 72 chars
const MEMBERSHIP_CACHE_TTL_MS = 60 * 1000;
const MEMBERSHIP_CACHE_MAX_SIZE = 1000;

/**
 * Mastra authentication provider for WorkOS.
 *
 * Uses WorkOS AuthKit with encrypted cookie-based sessions.
 * Sessions are stored in cookies, so they persist across server restarts.
 *
 * @example Basic usage with SSO
 * ```typescript
 * import { MastraAuthWorkos } from '@mastra/auth-workos';
 *
 * const auth = new MastraAuthWorkos({
 *   apiKey: process.env.WORKOS_API_KEY,
 *   clientId: process.env.WORKOS_CLIENT_ID,
 *   redirectUri: 'https://myapp.com/auth/callback',
 *   cookiePassword: process.env.WORKOS_COOKIE_PASSWORD, // min 32 chars
 * });
 * ```
 */
export class MastraAuthWorkos
  extends MastraAuthProvider<WorkOSUser>
  implements IUserProvider<EEUser>, ISSOProvider<EEUser>, ISessionProvider<Session>
{
  protected workos: WorkOS;
  protected clientId: string;
  protected redirectUri: string;
  protected ssoConfig: MastraAuthWorkosOptions['sso'];
  protected authService: AuthService<Request, Response>;
  protected config: AuthKitConfig;
  protected fetchMemberships: boolean;
  protected trustJwtClaims: boolean;
  protected jwtClaimOptions?: MastraAuthWorkosOptions['jwtClaims'];
  protected mapJwtPayloadToUser?: MastraAuthWorkosOptions['mapJwtPayloadToUser'];
  protected membershipCache: LRUCache<string, OrganizationMembership[]>;

  constructor(options?: MastraAuthWorkosOptions) {
    super({ name: options?.name ?? 'workos' });

    const apiKey = options?.apiKey ?? process.env.WORKOS_API_KEY;
    const clientId = options?.clientId ?? process.env.WORKOS_CLIENT_ID;
    const redirectUri = options?.redirectUri ?? process.env.WORKOS_REDIRECT_URI;
    const cookiePassword =
      options?.session?.cookiePassword ?? process.env.WORKOS_COOKIE_PASSWORD ?? DEV_COOKIE_PASSWORD;

    if (!apiKey || !clientId) {
      throw new Error(
        'WorkOS API key and client ID are required. ' +
          'Provide them in the options or set WORKOS_API_KEY and WORKOS_CLIENT_ID environment variables.',
      );
    }

    if (!redirectUri) {
      throw new Error(
        'WorkOS redirect URI is required. ' +
          'Provide it in the options or set WORKOS_REDIRECT_URI environment variable.',
      );
    }

    if (cookiePassword.length < 32) {
      throw new Error(
        'Cookie password must be at least 32 characters. ' +
          'Set WORKOS_COOKIE_PASSWORD environment variable or provide session.cookiePassword option.',
      );
    }

    this.clientId = clientId;
    this.redirectUri = redirectUri;
    this.ssoConfig = options?.sso;
    this.fetchMemberships = options?.fetchMemberships ?? false;
    this.trustJwtClaims = options?.trustJwtClaims ?? false;
    this.jwtClaimOptions = options?.jwtClaims;
    this.mapJwtPayloadToUser = options?.mapJwtPayloadToUser;
    this.membershipCache = new LRUCache<string, OrganizationMembership[]>({
      max: MEMBERSHIP_CACHE_MAX_SIZE,
      ttl: MEMBERSHIP_CACHE_TTL_MS,
    });

    // Create WorkOS client
    this.workos = new WorkOS(apiKey, { clientId });

    // Create AuthKit config
    this.config = {
      clientId,
      apiKey,
      redirectUri,
      cookiePassword,
      cookieName: options?.session?.cookieName ?? 'wos_session',
      cookieMaxAge: options?.session?.maxAge ?? 60 * 60 * 24 * 400, // 400 days
      cookieSameSite: options?.session?.sameSite?.toLowerCase() as 'lax' | 'strict' | 'none' | undefined,
      cookieDomain: undefined,
      apiHttps: true,
    };

    // Create session storage and auth service
    const storage = new WebSessionStorage(this.config);
    // Cast needed: @workos/authkit-session pins @workos-inc/node@8.0.0 but we use 8.8.0.
    // The runtime API is compatible; only private HttpClient types differ.
    this.authService = new AuthService(this.config, storage, this.workos as any, sessionEncryption);

    this.registerOptions(options as MastraAuthProviderOptions<WorkOSUser>);

    if (cookiePassword === DEV_COOKIE_PASSWORD) {
      console.warn(
        '[WorkOS] Using auto-generated cookie password for development. ' +
          'Sessions will not persist across server restarts. ' +
          'Set WORKOS_COOKIE_PASSWORD for persistent sessions.',
      );
    }
  }

  // ============================================================================
  // MastraAuthProvider Implementation
  // ============================================================================

  /**
   * Authenticate a bearer token or session cookie.
   *
   * Uses AuthKit's withAuth() for cookie-based sessions, falls back to
   * JWT verification for bearer tokens.
   */
  async authenticateToken(token: string, request: MastraAuthRequest): Promise<WorkOSUser | null> {
    try {
      // First try session-based auth via AuthKit
      const webRequest = getWebRequest(request);
      const { auth } = webRequest ? await this.authService.withAuth(webRequest) : { auth: { user: null } };

      if (auth.user) {
        // Fetch memberships only when FGA is configured (fetchMemberships: true).
        // Skipping this call avoids an extra network round-trip on every
        // authenticated request when FGA is not in use.
        let memberships: OrganizationMembership[] | undefined;
        if (this.fetchMemberships) {
          try {
            memberships = await this.getMemberships(auth.user.id);
          } catch {
            // Ignore membership fetch errors — FGA will gracefully degrade
          }
        }

        return {
          ...mapWorkOSUserToEEUser(auth.user),
          workosId: auth.user.id,
          organizationId: auth.organizationId,
          memberships,
        };
      }

      // Fall back to JWT verification for bearer tokens
      if (token) {
        const jwksUri = this.workos.userManagement.getJwksUrl(this.clientId);
        const payload = await verifyJwks(token, jwksUri);
        const jwtUser = this.resolveJwtPayloadUser(payload);

        if (this.trustJwtClaims && jwtUser?.id && jwtUser?.workosId) {
          return await this.attachMembershipsIfNeeded(jwtUser);
        }

        if (payload?.sub) {
          try {
            const user = await this.workos.userManagement.getUser(payload.sub);
            let memberships: OrganizationMembership[] | undefined;

            // Fetch memberships only when FGA is configured (fetchMemberships: true).
            if (this.fetchMemberships) {
              try {
                memberships = await this.getMemberships(user.id);
              } catch {
                memberships = undefined;
              }
            }

            return this.mergeJwtPayloadUser(
              {
                ...mapWorkOSUserToEEUser(user),
                workosId: user.id,
                organizationId: this.getSingleMembershipOrganizationId(memberships),
                memberships,
              },
              jwtUser,
              { trustOrganizationClaims: this.trustJwtClaims },
            );
          } catch {
            if (this.trustJwtClaims && jwtUser?.id && jwtUser?.workosId) {
              return await this.attachMembershipsIfNeeded(jwtUser);
            }
            return null;
          }
        }

        if (this.trustJwtClaims && jwtUser?.id && jwtUser?.workosId) {
          return await this.attachMembershipsIfNeeded(jwtUser);
        }
      }

      return null;
    } catch {
      return null;
    }
  }

  /**
   * Authorize a user for access.
   */
  async authorizeUser(user: WorkOSUser): Promise<boolean> {
    return !!user?.id && !!user?.workosId;
  }

  // ============================================================================
  // IUserProvider Implementation
  // ============================================================================

  /**
   * Get the current user from the request using AuthKit session.
   */
  async getCurrentUser(request: Request): Promise<EEUser | null> {
    try {
      const { auth, refreshedSessionData } = await this.authService.withAuth(request);

      if (!auth.user) {
        return null;
      }

      // Get organizationId from JWT claims, or fall back to fetching from memberships.
      // The fallback fetch is skipped when fetchMemberships is false (FGA not configured)
      // to avoid an extra network call on every authenticated request.
      let organizationId = auth.organizationId;
      let memberships: OrganizationMembership[] | undefined;
      if (this.fetchMemberships) {
        try {
          memberships = await this.getMemberships(auth.user.id);
          organizationId ??= this.getSingleMembershipOrganizationId(memberships);
        } catch {
          // Ignore membership fetch errors
        }
      }

      // Build user with session data
      const user: WorkOSUser = {
        ...mapWorkOSUserToEEUser(auth.user),
        workosId: auth.user.id,
        organizationId,
        memberships,
      };

      // If session was refreshed, attach to user object for caller to save
      if (refreshedSessionData) {
        (user as any)._refreshedSessionData = refreshedSessionData;
      }

      return user;
    } catch {
      return null;
    }
  }

  /**
   * Get a user by their ID.
   */
  async getUser(userId: string): Promise<WorkOSUser | null> {
    try {
      const user = await this.workos.userManagement.getUser(userId);
      return {
        ...mapWorkOSUserToEEUser(user),
        workosId: user.id,
      };
    } catch {
      return null;
    }
  }

  /**
   * Get the URL to the user's profile page.
   */
  getUserProfileUrl(user: EEUser): string {
    return `/profile/${user.id}`;
  }

  private async getMemberships(userId: string): Promise<OrganizationMembership[]> {
    const cached = this.membershipCache.get(userId);
    if (cached) {
      return cached;
    }

    try {
      const response = await this.workos.userManagement.listOrganizationMemberships({
        userId,
      });

      const memberships = await response.autoPagination();
      this.membershipCache.set(userId, memberships);
      return memberships;
    } catch (error) {
      this.membershipCache.delete(userId);
      throw error;
    }
  }

  private async attachMembershipsIfNeeded(user: WorkOSUser): Promise<WorkOSUser> {
    if (!this.fetchMemberships || user.organizationMembershipId) {
      return user;
    }

    try {
      const memberships = await this.getMemberships(user.workosId);
      return {
        ...user,
        organizationId: user.organizationId ?? this.getSingleMembershipOrganizationId(memberships),
        memberships,
      };
    } catch {
      return user;
    }
  }

  private getSingleMembershipOrganizationId(memberships?: OrganizationMembership[]): string | undefined {
    return memberships?.length === 1 ? memberships[0]?.organizationId : undefined;
  }

  private resolveJwtPayloadUser(payload: JwtPayload | null): WorkOSUser | null {
    if (!payload) {
      return null;
    }

    const mappedClaims = this.buildUserFromJwtClaims(payload);
    const customMappedClaims = this.mapJwtPayloadToUser?.(payload) ?? undefined;
    const combined = {
      ...(payload as Record<string, unknown>),
      ...(mappedClaims ?? {}),
      ...(customMappedClaims ?? {}),
    } as Partial<WorkOSUser> & Record<string, unknown>;

    const id = typeof combined.id === 'string' ? combined.id : undefined;
    const workosId = typeof combined.workosId === 'string' ? combined.workosId : id;
    if (!id || !workosId) {
      return null;
    }

    const metadata =
      combined.metadata && typeof combined.metadata === 'object' && !Array.isArray(combined.metadata)
        ? combined.metadata
        : undefined;

    return {
      ...combined,
      id,
      workosId,
      email: typeof combined.email === 'string' ? combined.email : undefined,
      name:
        typeof combined.name === 'string' ? combined.name : typeof combined.email === 'string' ? combined.email : id,
      organizationId: typeof combined.organizationId === 'string' ? combined.organizationId : undefined,
      organizationMembershipId:
        typeof combined.organizationMembershipId === 'string' ? combined.organizationMembershipId : undefined,
      metadata: {
        ...(metadata ?? {}),
        workosId,
        ...(typeof combined.organizationId === 'string' ? { organizationId: combined.organizationId } : {}),
        ...(typeof combined.organizationMembershipId === 'string'
          ? { organizationMembershipId: combined.organizationMembershipId }
          : {}),
      },
    };
  }

  private buildUserFromJwtClaims(payload: JwtPayload): Partial<WorkOSUser> | null {
    const userId = this.readJwtClaim(payload, this.jwtClaimOptions?.userId) ?? this.readJwtClaim(payload, 'sub');
    const workosId = this.readJwtClaim(payload, this.jwtClaimOptions?.workosId) ?? userId;

    if (!userId || !workosId) {
      return null;
    }

    return {
      id: userId,
      workosId,
      email: this.readJwtClaim(payload, this.jwtClaimOptions?.email) ?? this.readJwtClaim(payload, 'email'),
      name: this.readJwtClaim(payload, this.jwtClaimOptions?.name) ?? this.readJwtClaim(payload, 'name'),
      organizationId:
        this.readJwtClaim(payload, this.jwtClaimOptions?.organizationId) ?? this.readJwtClaim(payload, 'org_id'),
      organizationMembershipId: this.readJwtClaim(payload, this.jwtClaimOptions?.organizationMembershipId),
    };
  }

  private mergeJwtPayloadUser(
    user: WorkOSUser,
    jwtUser: WorkOSUser | null,
    options?: { trustOrganizationClaims?: boolean },
  ): WorkOSUser {
    if (!jwtUser) {
      return user;
    }

    const trustOrganizationClaims = options?.trustOrganizationClaims ?? true;
    const jwtMetadata = { ...(jwtUser.metadata ?? {}) };
    if (!trustOrganizationClaims) {
      delete jwtMetadata.organizationId;
      delete jwtMetadata.organizationMembershipId;
    }

    return {
      ...jwtUser,
      ...user,
      organizationId: trustOrganizationClaims ? (jwtUser.organizationId ?? user.organizationId) : user.organizationId,
      organizationMembershipId: trustOrganizationClaims
        ? (jwtUser.organizationMembershipId ?? user.organizationMembershipId)
        : user.organizationMembershipId,
      memberships: trustOrganizationClaims ? (user.memberships ?? jwtUser.memberships) : user.memberships,
      metadata: {
        ...jwtMetadata,
        ...(user.metadata ?? {}),
      },
    };
  }

  private readJwtClaim(payload: JwtPayload, claimPath?: string): string | undefined {
    if (!claimPath) {
      return undefined;
    }

    let current: unknown = payload;
    for (const segment of claimPath.split('.')) {
      if (!current || typeof current !== 'object' || !(segment in current)) {
        return undefined;
      }
      current = (current as Record<string, unknown>)[segment];
    }

    return typeof current === 'string' ? current : undefined;
  }

  // ============================================================================
  // ISSOProvider Implementation
  // ============================================================================

  /**
   * Get the URL to redirect users to for SSO login.
   */
  getLoginUrl(redirectUri: string, state: string): string {
    const baseOptions = {
      clientId: this.clientId,
      redirectUri: redirectUri || this.redirectUri,
      state,
    };

    if (this.ssoConfig?.connection) {
      return this.workos.userManagement.getAuthorizationUrl({
        ...baseOptions,
        connectionId: this.ssoConfig.connection,
      });
    } else if (this.ssoConfig?.provider) {
      return this.workos.userManagement.getAuthorizationUrl({
        ...baseOptions,
        provider: this.ssoConfig.provider,
      });
    } else if (this.ssoConfig?.defaultOrganization) {
      return this.workos.userManagement.getAuthorizationUrl({
        ...baseOptions,
        organizationId: this.ssoConfig.defaultOrganization,
      });
    }

    return this.workos.userManagement.getAuthorizationUrl({
      ...baseOptions,
      provider: 'authkit',
    });
  }

  /**
   * Handle the OAuth callback from WorkOS.
   *
   * Uses WorkOS SDK's authenticateWithCode directly instead of AuthKit's handleCallback.
   * AuthKit's handleCallback requires PKCE cookies that must be set during getLoginUrl()
   * and read during handleCallback(), but our ISSOProvider interface separates these
   * calls across different requests without cookie propagation.
   *
   * This approach was the original implementation before commit 6e4d4f5cf3 introduced
   * a regression by switching to AuthKit's handleCallback with dummy Request/Response
   * objects that couldn't provide the required PKCE cookies.
   */
  async handleCallback(code: string, _state: string): Promise<SSOCallbackResult<EEUser>> {
    // Use WorkOS SDK directly to exchange code for tokens (server-side, no PKCE required)
    const authResponse = await this.workos.userManagement.authenticateWithCode({
      clientId: this.clientId,
      code,
    });

    const user: WorkOSUser = {
      ...mapWorkOSUserToEEUser(authResponse.user),
      workosId: authResponse.user.id,
      organizationId: authResponse.organizationId,
    };

    // Create encrypted session cookie using AuthKit's encryption
    const sessionData = {
      accessToken: authResponse.accessToken,
      refreshToken: authResponse.refreshToken,
      user: authResponse.user,
      organizationId: authResponse.organizationId,
      impersonator: authResponse.impersonator,
    };

    // Use this.config for cookie settings to ensure consistency with read/clear paths
    const cookiePassword = this.config.cookiePassword;
    const cookieName = this.config.cookieName ?? 'wos_session';
    let cookies: string[] | undefined;

    if (cookiePassword) {
      const encryptedSession = await sessionEncryption.sealData(sessionData, { password: cookiePassword });
      // Set cookie with secure defaults matching AuthKit config
      const cookieOptions = [
        `${cookieName}=${encryptedSession}`,
        'Path=/',
        'HttpOnly',
        `SameSite=${this.config.cookieSameSite ?? 'Lax'}`,
        process.env['NODE_ENV'] === 'production' ? 'Secure' : '',
      ]
        .filter(Boolean)
        .join('; ');
      cookies = [cookieOptions];
    }

    return {
      user,
      tokens: {
        accessToken: authResponse.accessToken,
        refreshToken: authResponse.refreshToken,
      },
      cookies,
    };
  }

  /**
   * Get the URL to redirect users to for logout.
   * Extracts session ID from the request's JWT to build a valid WorkOS logout URL.
   *
   * @param redirectUri - URL to redirect to after logout
   * @param request - Request containing session cookie (needed to extract sid)
   * @returns Logout URL or null if no active session
   */
  async getLogoutUrl(redirectUri: string, request?: Request): Promise<string | null> {
    // WorkOS logout requires session_id from the JWT's sid claim
    if (!request) {
      return null;
    }

    try {
      const { auth } = await this.authService.withAuth(request);

      // No active session
      if (!auth.user) {
        return null;
      }

      // Decode JWT to extract sid claim (don't verify, just decode)
      const [, payloadBase64] = auth.accessToken.split('.');
      if (!payloadBase64) {
        return null;
      }

      const payload = JSON.parse(atob(payloadBase64));
      const sessionId = payload.sid;

      if (!sessionId) {
        return null;
      }

      return this.workos.userManagement.getLogoutUrl({ sessionId, returnTo: redirectUri });
    } catch {
      return null;
    }
  }

  /**
   * Get the configuration for rendering the login button.
   */
  getLoginButtonConfig(): SSOLoginConfig {
    let text = 'Sign in';
    if (this.ssoConfig?.provider) {
      const providerNames: Record<string, string> = {
        GoogleOAuth: 'Google',
        MicrosoftOAuth: 'Microsoft',
        GitHubOAuth: 'GitHub',
        AppleOAuth: 'Apple',
      };
      const providerName = providerNames[this.ssoConfig.provider];
      if (providerName) {
        text = `Sign in with ${providerName}`;
      }
    }

    return {
      provider: 'workos',
      text,
    };
  }

  // ============================================================================
  // ISessionProvider Implementation
  // ============================================================================

  /**
   * Create a new session for a user.
   *
   * Note: With AuthKit, sessions are created via handleCallback.
   * This method is kept for interface compatibility.
   */
  async createSession(userId: string, metadata?: Record<string, unknown>): Promise<Session> {
    const sessionId = crypto.randomUUID();
    const now = new Date();
    const expiresAt = new Date(now.getTime() + this.config.cookieMaxAge * 1000);

    return {
      id: sessionId,
      userId,
      createdAt: now,
      expiresAt,
      metadata,
    };
  }

  /**
   * Validate a session.
   *
   * With AuthKit, sessions are validated via withAuth().
   */
  async validateSession(_sessionId: string): Promise<Session | null> {
    // AuthKit handles validation internally via withAuth()
    // This method is kept for interface compatibility
    return null;
  }

  /**
   * Destroy a session.
   */
  async destroySession(_sessionId: string): Promise<void> {
    // AuthKit handles session clearing via signOut()
    // The actual cookie clearing happens in the response headers
  }

  /**
   * Refresh a session.
   */
  async refreshSession(_sessionId: string): Promise<Session | null> {
    // AuthKit handles refresh automatically in withAuth()
    return null;
  }

  /**
   * Extract session ID from a request.
   */
  getSessionIdFromRequest(_request: Request): string | null {
    // With AuthKit, we don't expose the session ID directly
    // The session is managed via encrypted cookies
    return null;
  }

  /**
   * Get response headers to set the session cookie.
   */
  getSessionHeaders(session: Session): Record<string, string> {
    // AuthKit handles cookie setting via saveSession()
    // Check for _sessionCookie from handleCallback
    const sessionCookie = (session as any)._sessionCookie;
    if (sessionCookie) {
      return { 'Set-Cookie': Array.isArray(sessionCookie) ? sessionCookie[0] : sessionCookie };
    }
    return {};
  }

  /**
   * Get response headers to clear the session cookie.
   */
  getClearSessionHeaders(): Record<string, string> {
    const cookieParts = [`${this.config.cookieName}=`, 'Path=/', 'Max-Age=0', 'HttpOnly'];
    return { 'Set-Cookie': cookieParts.join('; ') };
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  /**
   * Get the underlying WorkOS client.
   */
  getWorkOS(): WorkOS {
    return this.workos;
  }

  /**
   * Get the AuthKit AuthService.
   */
  getAuthService(): AuthService<Request, Response> {
    return this.authService;
  }

  /**
   * Get the configured client ID.
   */
  getClientId(): string {
    return this.clientId;
  }

  /**
   * Get the configured redirect URI.
   */
  getRedirectUri(): string {
    return this.redirectUri;
  }
}
